/**
 * R4's Schedule tab, in the legacy app's shape (Scores UX spec v1.0 section
 * 8): the whole season oldest first, grouped under month headers ("Oct
 * 2026", `caption` 600 `textSecondary`, sticky), one `TeamScheduleRow` per
 * game. The tab opens scrolled to the next game, or to the last game played
 * once the season is over, so a fan lands on what matters now instead of on
 * week 1.
 *
 * Which game is next is `teamScheduleLogic.ts`'s call (`nextTeamGame`), and a
 * game the server no longer serves as live sorts with the rest by kickoff
 * under its server label (ISSUE-1148). This file only groups by the kickoff's
 * calendar day in the viewer's zone and never re-derives a status.
 *
 * A failed read shows the failed notice, never the empty line (ISSUE-1021).
 */
import { type ReactNode, useEffect, useRef } from "react";

import { monthShort } from "../scores/calendarDay";
import {
  toTeamGameRowModel,
  type typeTeamGameRowModel,
  type typeTeamScheduleEvent,
} from "./teamGameLogic";
import { TEAM_SCHEDULE_FAILED_LINE } from "./teamProfileLogic";
import { TeamReadFailed } from "./TeamReadFailed";
import { nextTeamGame, scheduleResults } from "./teamScheduleLogic";
import { TeamScheduleRow } from "./TeamScheduleRow";

export const TEAM_SCHEDULE_EMPTY = "No games scheduled yet.";

export type typeTeamScheduleTabProps = {
  teamId: string;
  games: readonly typeTeamScheduleEvent[];
  timeZone: string;
  onOpenGame: (eventId: string) => void;
  /** True when the schedule read failed. Wins over the game list and the empty line. */
  isFailed: boolean;
  onRetry: () => void;
};

/** "Oct 2026" for a "YYYY-MM-DD" day, or "" for none. Intl free. */
export function scheduleMonthLabel(day: string): string {
  return day === "" ? "" : `${monthShort(day)} ${day.slice(0, 4)}`;
}

/** The season oldest first, in runs of one calendar month. */
export function groupScheduleByMonth(
  rows: readonly typeTeamGameRowModel[],
): { key: string; label: string; rows: typeTeamGameRowModel[] }[] {
  const groups: { key: string; label: string; rows: typeTeamGameRowModel[] }[] =
    [];
  for (const row of rows) {
    const key = row.day.slice(0, 7);
    const current = groups[groups.length - 1];
    if (current?.key === key) {
      current.rows.push(row);
    } else {
      groups.push({ key, label: scheduleMonthLabel(row.day), rows: [row] });
    }
  }
  return groups;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export const TeamScheduleTab = (props: typeTeamScheduleTabProps): ReactNode => {
  const ordered = [...props.games].sort(
    (a, b) => new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime(),
  );
  const rows = ordered.map((event) =>
    toTeamGameRowModel({
      event,
      teamId: props.teamId,
      timeZone: props.timeZone,
    }),
  );
  const groups = groupScheduleByMonth(rows);
  const anchorEventId =
    nextTeamGame(props.games)?.eventId ??
    scheduleResults(props.games)[0]?.eventId ??
    null;

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    const element = anchorRef.current;
    if (element === null || typeof element.scrollIntoView !== "function")
      return;
    element.scrollIntoView({
      block: "center",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [anchorEventId]);

  return (
    <section
      data-testid="team-schedule-tab"
      data-anchor-event-id={anchorEventId ?? undefined}
      className="rounded-md border border-hairline bg-surface"
    >
      {props.isFailed ? (
        <TeamReadFailed
          layout="panel"
          testId="team-schedule-failed"
          line={TEAM_SCHEDULE_FAILED_LINE}
          onRetry={props.onRetry}
        />
      ) : rows.length === 0 ? (
        <p
          data-testid="team-schedule-empty"
          className="px-4 py-3 text-body text-ink-secondary"
        >
          {TEAM_SCHEDULE_EMPTY}
        </p>
      ) : (
        groups.map((group) => (
          <div
            key={group.key}
            data-testid="team-schedule-month"
            className="flex flex-col"
          >
            <h3
              data-testid="team-schedule-month-header"
              className="sticky top-0 z-10 rounded-t-md border-b border-hairline bg-surface px-4 pt-3 pb-1 text-caption text-ink-secondary"
            >
              {group.label}
            </h3>
            {group.rows.map((row) => (
              <TeamScheduleRow
                key={row.eventId}
                row={row}
                onOpenGame={props.onOpenGame}
                rowRef={
                  row.eventId === anchorEventId
                    ? (element) => {
                        anchorRef.current = element;
                      }
                    : undefined
                }
              />
            ))}
          </div>
        ))
      )}
    </section>
  );
};
